// Vorauswahl-Formular (Etappe 1.5, Task 1) — läuft im Formularbereich der Dynamic
// Island ("Pille", variant="pille"-Muster wie AuftragVorbereitenModal.tsx), geöffnet
// über openForm() aus UebergabeListe.tsx. Vor Ort beginnt der Assistent damit oft ohne
// Netz — deshalb ist hier nichts außer der Art Pflicht: Objekt/Einheit/Kontakte lassen
// sich auch noch in Schritt 1 des Assistenten nachtragen oder korrigieren.
//
// Die Pille liefert bereits Titelzeile + Schließen-Button (s. FormContent.tsx) — dieses
// Formular rendert deshalb kein eigenes Overlay/Header, nur scrollbaren Inhalt +
// fixierten Footer (Muster: AuftragVorbereitenModal.tsx `pille`-Zweig).
//
// Optik bewusst NICHT das dunkle Pillen-Theme (bg-white/5, weiße Schrift), sondern das
// helle Frosted Glass, das im restlichen Übergabe-Modul etabliert ist (SchrittRahmen.tsx/
// UebergabeListe.tsx: bg-white/45 backdrop-blur-2xl, Gold #c4a77d für aktive Auswahl,
// dunkle Handlungs-Buttons) — Vorgabe des Auftraggebers, damit die Vorauswahl optisch
// zum Rest des Moduls passt statt zum generischen Pillen-Look.
//
// Fix-Runde 1 (Review): bei `kauf` kommen BEIDE Seiten aus den Eigentümern — die
// Mieter-Auswahl (mit dem AKTUELLEN Mieter der Einheit gefüllt) hätte dort fast immer
// die falsche Person angeboten. Die Mieter-Auswahl erscheint deshalb bei `kauf` gar
// nicht mehr, stattdessen zeigt `kauf` ZWEI Eigentümer-Felder: den bisherigen
// Eigentümer (Übergeber) und den Käufer (Übernehmer, „Käufer", falls schon als
// Eigentümer angelegt — sonst leer, dann in Schritt 1 von Hand eintragen). Beim
// Wechsel der Art werden alle bisher getroffenen Kontakt-Auswahlen zurückgesetzt,
// damit nichts aus einer anderen Konstellation (z. B. ein bereits gewählter Mieter)
// unbemerkt als Käufer stehen bleibt.
//
// Fix-Runde 2 (Praxis-Feedback vom Tablet): Mieter (~1060) und Eigentümer (~218) waren
// als aufgeklappte <select>-Liste auf dem Tablet nicht mehr bedienbar — die Liste füllte
// den ganzen Bildschirm, Scrollen zu "Lindeholz" war eine Zumutung. Beide Felder sind
// jetzt Suchfelder mit Vorschlägen (Muster: TicketFormular.tsx „Mieter/Objekt suchen",
// TicketVerknuepfungen.tsx „Ticket suchen"): serverseitige ilike-Suche, entprellt, erst
// ab lib/uebergabe/personenSuche.ts::MIN_SUCHLAENGE Zeichen — es wird NIE die ganze
// Tabelle geladen. Ohne Eingabe zeigt das Mieter-Feld weiterhin (nur) den zur gewählten
// Einheit passenden laufenden Vertrag als Vorschlag (bisher: ladeMieter() mit Fallback
// „sonst ALLE Mieter" — genau dieser Fallback war der Bug und entfällt ersatzlos, das
// Feld zeigt dann einfach „Zum Suchen tippen"). Die reine Filter-/Anzeigelogik
// (ilike-Bedingung, Schwellwert, Ausblenden der jeweils anderen Auswahl, Anzeigename)
// steckt testbar in personenSuche.ts; die Netzwerk-Suchfunktionen unten bleiben
// ungetestet wie ihre Vorbilder in TicketFormular.tsx/TicketVerknuepfungen.tsx (auch dort
// wird ein Suchfehler nicht extra behandelt, sondern die Ergebnisliste bleibt einfach leer).
import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Buildings, CheckCircle, CircleNotch, Door, MagnifyingGlass, X } from '@phosphor-icons/react';
import { ArtKacheln } from './liste/ListenTeile';
import { supabase } from '../../../lib/supabase/client';
import { eigentuemerAnzeigename } from '../../../lib/eigentuemer/anzeige';
import { passtZurEinheit } from '../../../lib/uebergabe/einheitVergleich';
import {
  MAX_VORSCHLAEGE,
  mieterSuchBedingung,
  eigentuemerSuchBedingung,
  sollServerSuchen,
  ohneAndereAuswahl,
  begrenze,
  mieterAnzeigename,
  type PersonOption,
} from '../../../lib/uebergabe/personenSuche';
import type { Art } from '../../../lib/uebergabe/typen';
import type { Bezug } from '../../../lib/uebergabe/bezug';
import LadeHinweis from './LadeHinweis';

interface UebergabeVorauswahlProps {
  onClose: () => void;
  onStart: (bezug: Bezug) => void;
}

interface ObjektOption { id: string; bezeichnung: string; strasse: string; hausnummer: string; plz: string; stadt: string }
interface EinheitOption { id: string; bezeichnung: string }

const ART_OPTIONEN: { wert: Art; label: string }[] = [
  { wert: 'einzug', label: 'Einzug' },
  { wert: 'auszug', label: 'Auszug' },
  { wert: 'kauf', label: 'Übergabe an Eigentümer' },
];

// Welche Rolle die Mieter-Auswahl bei Einzug/Auszug ergibt (s. verteileRollen) — nur
// für den kleinen Hinweistext unter dem Feld, keine eigene Logik. Bei "kauf" entfällt
// die Mieter-Auswahl komplett (s. Kopf-Kommentar), deshalb kein Eintrag dafür nötig.
const MIETER_ROLLE_LABEL: Record<'einzug' | 'auszug', string> = { einzug: 'Übernehmer', auszug: 'Übergeber' };

// Token-Stil (design_handoff_uebergabe §3)
const LABEL: CSSProperties = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink3)', marginBottom: 8 };
const FELD: CSSProperties = { width: '100%', height: 46, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--fill2)',
  color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, minWidth: 0 };
const ROLLE_HINWEIS: CSSProperties = { margin: '6px 0 0', fontSize: 12, fontWeight: 800, color: 'var(--acc)' };
const LISTE: CSSProperties = { marginTop: 6, maxHeight: 240, overflowY: 'auto', borderRadius: 16, background: 'var(--glass)', boxShadow: 'var(--glass-sh)' };
const LISTEN_ZEILE: CSSProperties = { width: '100%', minHeight: 44, padding: '0 14px', gap: 10, border: 'none', borderBottom: '1px solid var(--line)', background: 'transparent',
  color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, textAlign: 'left' };
const HINWEIS_TEXT: CSSProperties = { margin: '6px 0 0', fontSize: 12, fontWeight: 600, color: 'var(--ink3)' };

// Sucheingabe entprellen (Muster: TicketFormular.tsx/VertragFormular.tsx, 250ms), damit
// nicht bei jedem Tastendruck eine Anfrage rausgeht.
const SUCH_VERZOEGERUNG_MS = 250;

interface PersonSucheFeldProps {
  label: string;
  platzhalter: string;
  ausgewaehlt: PersonOption | null;
  onWaehle: (p: PersonOption | null) => void;
  /** Vorschläge ohne Eingabe (z. B. der laufende Vertrag der gewählten Einheit) — bewusst klein. */
  vorschlaege: PersonOption[];
  vorschlaegeLaden?: boolean;
  vorschlaegeFehler?: boolean;
  aufVorschlaegeErneutVersuchen?: () => void;
  /** Serverseitige ilike-Suche (Ergebnis bereits auf MAX_VORSCHLAEGE begrenzt). */
  sucheServer: (query: string) => Promise<PersonOption[]>;
  /** Rollen-/Zusatzhinweis unter dem Feld — vom Aufrufer berechnet, unverändert übernommen. */
  hinweis?: React.ReactNode;
}

const PersonSucheFeld: React.FC<PersonSucheFeldProps> = ({
  label, platzhalter, ausgewaehlt, onWaehle, vorschlaege, vorschlaegeLaden, vorschlaegeFehler,
  aufVorschlaegeErneutVersuchen, sucheServer, hinweis,
}) => {
  const [suchtext, setSuchtext] = useState('');
  const [ergebnisse, setErgebnisse] = useState<PersonOption[]>([]);
  const [suchLaedt, setSuchLaedt] = useState(false);

  useEffect(() => {
    if (!sollServerSuchen(suchtext)) { setErgebnisse([]); setSuchLaedt(false); return; }
    setSuchLaedt(true);
    const timer = setTimeout(() => {
      sucheServer(suchtext.trim())
        .then(setErgebnisse)
        .finally(() => setSuchLaedt(false));
    }, SUCH_VERZOEGERUNG_MS);
    return () => clearTimeout(timer);
  }, [suchtext, sucheServer]);

  if (ausgewaehlt) {
    return (
      <div>
        <span style={LABEL}>{label}</span>
        <div className="flex items-center" style={{ ...FELD, gap: 8, paddingRight: 4, background: 'var(--acc-soft)', boxShadow: 'inset 0 0 0 1.5px var(--acc)' }}>
          <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 800 }}>{ausgewaehlt.name}</span>
          <button type="button" onClick={() => onWaehle(null)} aria-label={`${label}-Auswahl entfernen`} data-tip={`${label}-Auswahl entfernen`}
            className="inline-flex shrink-0 items-center justify-center" style={{ width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--acc)' }}>
            <X size={15} weight="bold" />
          </button>
        </div>
        {hinweis}
      </div>
    );
  }

  // Ohne Eingabe: die (kleine) Vorschlagsliste — sonst die debounced Suchergebnisse.
  const zeigeVorschlaege = !sollServerSuchen(suchtext);
  const angezeigt = zeigeVorschlaege ? vorschlaege : ergebnisse;

  return (
    <div>
      <span style={LABEL}>{label}</span>
      <div className="relative flex items-center" style={{ ...FELD, gap: 8, padding: '0 12px' }}>
        <MagnifyingGlass size={15} color="var(--ink3)" className="shrink-0" />
        <input type="text" value={suchtext} onChange={(e) => setSuchtext(e.target.value)}
          // Esc leert zuerst die Suche, erst ein zweites Esc schließt das Sheet.
          onKeyDown={(e) => { if (e.key === 'Escape' && suchtext) { e.stopPropagation(); setSuchtext(''); } }} placeholder={platzhalter} aria-label={label}
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
        {suchLaedt && <CircleNotch size={14} weight="bold" color="var(--ink3)" className="shrink-0 animate-spin" />}
      </div>

      {vorschlaegeFehler && zeigeVorschlaege ? (
        <div style={{ marginTop: 6 }}>
          <LadeHinweis text="Vorschlag konnte nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={aufVorschlaegeErneutVersuchen ?? (() => {})} />
        </div>
      ) : angezeigt.length > 0 ? (
        <div className="immo-scroll" style={LISTE}>
          {angezeigt.map((p) => (
            <button key={p.id} type="button" onClick={() => { onWaehle(p); setSuchtext(''); }} className="immo-menue-zeile flex items-center" style={LISTEN_ZEILE}>
              {p.name}
            </button>
          ))}
        </div>
      ) : zeigeVorschlaege ? (
        <p style={HINWEIS_TEXT}>{vorschlaegeLaden ? 'Lädt…' : 'Zum Suchen tippen'}</p>
      ) : !suchLaedt ? (
        <p style={HINWEIS_TEXT}>Keine Treffer</p>
      ) : null}

      {hinweis}
    </div>
  );
};

const UebergabeVorauswahl: React.FC<UebergabeVorauswahlProps> = ({ onClose, onStart }) => {
  const [art, setArt] = useState<Art | null>(null);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [einheitId, setEinheitId] = useState<string | null>(null);
  const [einheitBezeichnung, setEinheitBezeichnung] = useState<string | null>(null);
  const [mieterAuswahl, setMieterAuswahl] = useState<PersonOption | null>(null);
  // Bei "kauf": eigentuemerAuswahl = bisheriger Eigentümer (Übergeber), kaeuferAuswahl =
  // Käufer (Übernehmer, zweiter Eigentümer-Kontakt — s. Kopf-Kommentar).
  const [eigentuemerAuswahl, setEigentuemerAuswahl] = useState<PersonOption | null>(null);
  const [kaeuferAuswahl, setKaeuferAuswahl] = useState<PersonOption | null>(null);

  const [objekte, setObjekte] = useState<ObjektOption[]>([]);
  const [objekteFehler, setObjekteFehler] = useState(false);
  const [einheiten, setEinheiten] = useState<EinheitOption[]>([]);
  const [einheitenLaden, setEinheitenLaden] = useState(false);
  const [einheitenFehler, setEinheitenFehler] = useState(false);

  // Mieter-Vorschlag ohne Eingabe: NUR der laufende Vertrag der gewählten Einheit —
  // kein Fallback mehr auf "alle Mieter" (das war der Bug, s. Kopf-Kommentar).
  const [mieterVorschlaege, setMieterVorschlaege] = useState<PersonOption[]>([]);
  const [mieterVorschlaegeLaden, setMieterVorschlaegeLaden] = useState(false);
  const [mieterVorschlaegeFehler, setMieterVorschlaegeFehler] = useState(false);

  const ladeObjekte = useCallback(async () => {
    setObjekteFehler(false);
    const { data, error } = await supabase
      .from('objekte').select('id, bezeichnung, strasse, hausnummer, plz, stadt').order('bezeichnung');
    if (error) { setObjekteFehler(true); return; }
    setObjekte((data ?? []) as ObjektOption[]);
  }, []);
  useEffect(() => { void ladeObjekte(); }, [ladeObjekte]);

  const ladeEinheiten = useCallback(async () => {
    if (!objektId) { setEinheiten([]); setEinheitenFehler(false); return; }
    setEinheitenLaden(true);
    setEinheitenFehler(false);
    const { data, error } = await supabase
      .from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', objektId).order('bezeichnung');
    setEinheitenLaden(false);
    if (error) { setEinheitenFehler(true); return; }
    setEinheiten((data ?? []) as EinheitOption[]);
  }, [objektId]);
  useEffect(() => { void ladeEinheiten(); }, [ladeEinheiten]);

  // Vorbelegung: laufender Vertrag der gewählten Einheit (vertraege.einheit ist Freitext,
  // kein FK — Abgleich tolerant über passtZurEinheit(), wie in SchrittRahmen.tsx, da die
  // Schreibweise in den Stammdaten uneinheitlich ist). Nur relevant für einzug/auszug —
  // bei "kauf" wird die Mieter-Auswahl gar nicht gezeigt (s. Kopf-Kommentar). Findet sich
  // kein Vertrag, bleibt die Vorschlagsliste leer — es wird NICHT mehr auf "alle Mieter"
  // ausgewichen (das füllte vorher den ganzen Bildschirm).
  const ladeMieterVorschlaege = useCallback(async () => {
    if (art === 'kauf' || !objektId || !einheitBezeichnung) {
      setMieterVorschlaege([]); setMieterVorschlaegeFehler(false); return;
    }
    setMieterVorschlaegeLaden(true);
    setMieterVorschlaegeFehler(false);
    try {
      const { data: vertragsZeilen, error: vertragsFehler } = await supabase
        .from('vertraege').select('mieter_id, einheit')
        .eq('objekt_id', objektId).eq('status', 'aktiv');
      if (vertragsFehler) { setMieterVorschlaegeFehler(true); return; }
      const mieterIds = (vertragsZeilen ?? [])
        .filter((v) => passtZurEinheit(v.einheit, einheitBezeichnung))
        .map((v) => v.mieter_id)
        .filter((id): id is string => !!id);
      if (mieterIds.length === 0) { setMieterVorschlaege([]); return; }
      const { data, error } = await supabase
        .from('mieter').select('id, vorname, nachname, firma_name').in('id', mieterIds);
      if (error) { setMieterVorschlaegeFehler(true); return; }
      setMieterVorschlaege(begrenze((data ?? []).map((m) => ({ id: m.id, name: mieterAnzeigename(m) }))));
    } finally {
      setMieterVorschlaegeLaden(false);
    }
  }, [art, objektId, einheitBezeichnung]);
  useEffect(() => { void ladeMieterVorschlaege(); }, [ladeMieterVorschlaege]);

  // Serverseitige Mieter-Suche (Muster: TicketFormular.tsx) — läuft erst ab
  // MIN_SUCHLAENGE Zeichen (s. PersonSucheFeld), holt nie die ganze Tabelle.
  const sucheMieter = useCallback(async (query: string): Promise<PersonOption[]> => {
    const { data } = await supabase
      .from('mieter').select('id, vorname, nachname, firma_name')
      .or(mieterSuchBedingung(query)).order('nachname').limit(MAX_VORSCHLAEGE);
    return (data ?? []).map((m) => ({ id: m.id, name: mieterAnzeigename(m) }));
  }, []);

  // Serverseitige Eigentümer-Suche — blendet die jeweils andere getroffene Auswahl aus
  // (Käufer ↔ bisheriger Eigentümer dürfen nicht dieselbe Person sein, Fix-Runde 1).
  const sucheEigentuemer = useCallback(async (query: string, andereId: string | null): Promise<PersonOption[]> => {
    const { data } = await supabase
      .from('eigentuemer').select('id, anrede, vorname, nachname, firma_name')
      .or(eigentuemerSuchBedingung(query)).order('nachname').limit(MAX_VORSCHLAEGE);
    const optionen = (data ?? []).map((e) => ({ id: e.id, name: eigentuemerAnzeigename(e) }));
    return begrenze(ohneAndereAuswahl(optionen, andereId));
  }, []);
  const sucheBisherigerEigentuemer = useCallback(
    (query: string) => sucheEigentuemer(query, kaeuferAuswahl?.id ?? null),
    [sucheEigentuemer, kaeuferAuswahl],
  );
  const sucheKaeufer = useCallback(
    (query: string) => sucheEigentuemer(query, eigentuemerAuswahl?.id ?? null),
    [sucheEigentuemer, eigentuemerAuswahl],
  );

  // Beim Wechsel der Art ALLE Kontakt-Auswahlen zurücksetzen — sonst bliebe z. B. ein
  // bereits gewählter Mieter beim Umschalten auf "kauf" unbemerkt als Bezugsfeld stehen
  // (Fix-Runde 1, Review).
  const aendereArt = (neu: Art) => {
    setArt(neu);
    setMieterAuswahl(null);
    setEigentuemerAuswahl(null);
    setKaeuferAuswahl(null);
  };
  const aendereObjekt = (id: string) => {
    setObjektId(id || null);
    setEinheitId(null);
    setEinheitBezeichnung(null);
    setMieterAuswahl(null);
  };
  const aendereEinheit = (id: string) => {
    setEinheitId(id || null);
    setEinheitBezeichnung(einheiten.find((e) => e.id === id)?.bezeichnung ?? null);
    setMieterAuswahl(null);
  };

  const objektAusgewaehlt = useMemo(() => objekte.find((o) => o.id === objektId) ?? null, [objekte, objektId]);

  const handleStart = () => {
    if (!art) return;
    const istKauf = art === 'kauf';
    onStart({
      art,
      objekt_id: objektId,
      einheit_id: einheitId,
      // Bezugsfelder müssen zur Art passen: bei "kauf" kein Mieter (der aktuelle
      // Mieter der Einheit hat mit einem Eigentümerwechsel nichts zu tun).
      mieter_id: istKauf ? null : mieterAuswahl?.id ?? null,
      eigentuemer_id: istKauf ? eigentuemerAuswahl?.id ?? null : null,
      kaeufer_eigentuemer_id: istKauf ? kaeuferAuswahl?.id ?? null : null,
    });
  };

  // Objektwahl als Suche mit Trefferliste (Handoff §3) — dieselbe Liste wie zuvor im Auswahlfeld, nur gefiltert.
  const [objektSuche, setObjektSuche] = useState('');
  const objektTreffer = useMemo(() => {
    const q = objektSuche.trim().toLowerCase();
    if (!q) return objekte;
    return objekte.filter((o) => `${o.bezeichnung} ${o.strasse} ${o.hausnummer} ${o.plz} ${o.stadt}`.toLowerCase().includes(q));
  }, [objekte, objektSuche]);

  return (
    <div className="flex flex-col" style={{ gap: 18 }}>
      <div>
        <span style={LABEL}>Art der Übergabe</span>
        <ArtKacheln wert={art} onWahl={aendereArt} />
      </div>

      <div>
        <span style={LABEL}>Objekt</span>
        {objekteFehler ? (
          <LadeHinweis text="Objekte konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeObjekte} />
        ) : objektAusgewaehlt ? (
          <div className="flex items-center" style={{ ...FELD, height: 'auto', minHeight: 56, padding: '8px 4px 8px 12px', gap: 10, background: 'var(--glass)', boxShadow: 'var(--glass-sh)' }}>
            <Buildings size={18} weight="fill" color="var(--acc)" className="shrink-0" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate" style={{ fontWeight: 800 }}>{objektAusgewaehlt.bezeichnung}</span>
              <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>
                {objektAusgewaehlt.strasse} {objektAusgewaehlt.hausnummer}, {objektAusgewaehlt.plz} {objektAusgewaehlt.stadt}
              </span>
            </span>
            <button type="button" onClick={() => { aendereObjekt(''); setObjektSuche(''); }} aria-label="Objekt ändern" data-tip="Objekt ändern"
              className="inline-flex shrink-0 items-center justify-center" style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--ink2)' }}>
              <X size={15} weight="bold" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center" style={{ ...FELD, gap: 8, padding: '0 12px' }}>
              <MagnifyingGlass size={15} color="var(--ink3)" className="shrink-0" />
              <input value={objektSuche} onChange={(e) => setObjektSuche(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape' && objektSuche) { e.stopPropagation(); setObjektSuche(''); } }} placeholder="Objekt wählen…" aria-label="Objekt"
                className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
            </div>
            {objektTreffer.length > 0 ? (
              <div className="immo-scroll" style={LISTE}>
                {objektTreffer.map((o) => (
                  <button key={o.id} type="button" onClick={() => aendereObjekt(o.id)} className="immo-menue-zeile flex items-center" style={LISTEN_ZEILE}>
                    <Buildings size={16} weight="fill" color="var(--acc)" className="shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{o.bezeichnung}</span>
                    <span className="shrink-0" style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{o.plz} {o.stadt}</span>
                  </button>
                ))}
              </div>
            ) : objektSuche.trim() ? <p style={HINWEIS_TEXT}>Keine Treffer</p> : null}
          </>
        )}
      </div>

      {objektId && (
        <div>
          <span style={LABEL}>Einheit</span>
          {einheitenFehler ? (
            <LadeHinweis text="Einheiten konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeEinheiten} />
          ) : einheitenLaden ? (
            <p style={HINWEIS_TEXT}>Lädt…</p>
          ) : (
            <div role="radiogroup" aria-label="Einheit" className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
              {einheiten.map((e) => {
                const an = einheitId === e.id;
                return (
                  // Erneut tippen hebt die Auswahl auf — entspricht der leeren Option „Einheit wählen…“ im bisherigen Auswahlfeld.
                  <button key={e.id} type="button" role="radio" aria-checked={an} onClick={() => aendereEinheit(an ? '' : e.id)}
                    className="flex items-center" style={{ minHeight: 48, padding: '8px 12px', gap: 8, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 13.5, fontWeight: 800, textAlign: 'left', color: an ? 'var(--acc-ink)' : 'var(--ink)',
                      background: an ? 'var(--acc-fill)' : 'var(--fill2)', boxShadow: an ? 'var(--acc-sh)' : 'none' }}>
                    {an ? <CheckCircle size={16} weight="fill" className="shrink-0" /> : <Door size={16} weight="fill" className="shrink-0" color="var(--ink3)" />}
                    <span className="min-w-0 break-words">{e.bezeichnung}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {art !== 'kauf' && (
        <PersonSucheFeld
          key={`mieter-${art ?? ''}-${objektId ?? ''}-${einheitId ?? ''}`}
          label="Mieter"
          platzhalter="Mieter suchen…"
          ausgewaehlt={mieterAuswahl}
          onWaehle={setMieterAuswahl}
          vorschlaege={mieterVorschlaege}
          vorschlaegeLaden={mieterVorschlaegeLaden}
          vorschlaegeFehler={mieterVorschlaegeFehler}
          aufVorschlaegeErneutVersuchen={ladeMieterVorschlaege}
          sucheServer={sucheMieter}
          hinweis={art && mieterAuswahl && (
            <p style={ROLLE_HINWEIS}>→ wird {MIETER_ROLLE_LABEL[art as 'einzug' | 'auszug']}</p>
          )}
        />
      )}

      {art === 'kauf' && (
        <>
          <PersonSucheFeld
            label="Eigentümer (bisherig)"
            platzhalter="Eigentümer suchen…"
            ausgewaehlt={eigentuemerAuswahl}
            onWaehle={setEigentuemerAuswahl}
            vorschlaege={[]}
            sucheServer={sucheBisherigerEigentuemer}
            hinweis={eigentuemerAuswahl && <p style={ROLLE_HINWEIS}>→ wird Übergeber</p>}
          />
          <PersonSucheFeld
            label="Käufer"
            platzhalter="Käufer suchen (optional)…"
            ausgewaehlt={kaeuferAuswahl}
            onWaehle={setKaeuferAuswahl}
            vorschlaege={[]}
            sucheServer={sucheKaeufer}
            hinweis={kaeuferAuswahl ? (
              <p style={ROLLE_HINWEIS}>→ wird Übernehmer</p>
            ) : (
              <p style={HINWEIS_TEXT}>Noch nicht als Eigentümer angelegt? Name/Kontakt in Schritt 1 des Assistenten eintragen.</p>
            )}
          />
        </>
      )}

      <div className="flex justify-end" style={{ gap: 10, paddingTop: 4 }}>
        <button type="button" onClick={onClose}
          style={{ height: 46, padding: '0 18px', borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
          Abbrechen
        </button>
        <button type="button" onClick={handleStart} disabled={!art}
          style={{ height: 46, padding: '0 20px', borderRadius: 15, border: 'none', cursor: art ? 'pointer' : 'default', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
            background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: art ? 'var(--acc-sh)' : 'none', opacity: art ? 1 : 0.45 }}>
          Übergabe starten
        </button>
      </div>
    </div>
  );
};

export default UebergabeVorauswahl;
